import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";
import { SectionBadge } from "@/components/Comparison";
import { CASE_STUDIES } from "@/data/seoContent";
import { ArrowRight, Star, Trophy } from "lucide-react";

export default function CaseStudy() {
  const { slug } = useParams();
  const c = CASE_STUDIES.find((x) => x.slug === slug);
  const title = c ? `${c.company} · SwiftCash Case Study` : "Case Study · SwiftCash";
  const description = c ? `${c.company} — ${c.focus}. ${c.quote.slice(0, 130)}…` : "SwiftCash case study.";
  const jsonLd = c ? {
    "@context": "https://schema.org",
    "@type": "Review",
    "reviewBody": c.quote,
    "author": { "@type": "Person", "name": c.author },
    "itemReviewed": { "@type": "Organization", "name": "SwiftCash", "url": "https://swiftca.sh" },
    "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
  } : null;
  useSeo({ title, description, path: `/case-studies/${slug || ""}`, jsonLd });
  if (!c) return <Navigate to="/" replace />;

  return (
    <div className="bg-white text-navy min-h-screen">
      <Navbar />
      <section className="hero-grad-dark text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 dots-dark opacity-40 pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 md:px-8 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[10.5px] uppercase tracking-[0.14em] font-bold mb-5">
            <Trophy className="h-3 w-3" strokeWidth={2.6} /> {c.kind === "lender" ? "Lender case study" : "Funded case study"}
          </div>
          <h1 className="font-display font-extrabold text-[40px] md:text-[60px] leading-[1] tracking-[-0.035em]">{c.company}</h1>
          <div className="mt-3 text-[14px] text-white/70 font-semibold">{c.focus}</div>
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {c.stats.map((s) => (
              <div key={s.l} className="rounded-lg bg-white/5 border border-white/10 px-3 py-3 text-center">
                <div className="font-display font-extrabold text-[18px] tracking-[-0.02em] whitespace-nowrap">{s.v}</div>
                <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-white/55 mt-1 font-bold">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="rounded-2xl bg-emerald-soft/60 border border-emerald/20 p-6 mb-10">
            <div className="flex items-center gap-1 mb-3">
              {[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-amber text-amber" />)}
            </div>
            <p className="text-[16px] leading-[1.6] text-navy font-semibold italic">&ldquo;{c.quote}&rdquo;</p>
            <div className="mt-3 text-[11.5px] text-navy/55 font-mono uppercase tracking-[0.1em] font-bold">{c.author}</div>
          </div>
          <div className="space-y-5">
            {c.story.map((p, i) => <p key={i} className="text-[16px] leading-[1.75] text-navy/80 font-medium">{p}</p>)}
          </div>
          <div className="mt-12 pt-8 border-t border-line flex flex-col sm:flex-row gap-3">
            <Link to={c.kind === "lender" ? "/lender/signup" : "/apply"} data-testid="cs-cta"
              className="btn-shine group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-emerald text-white font-extrabold text-[14px] hover:bg-emerald-2 shadow-emerald flex-1">
              {c.kind === "lender" ? "Apply to join the panel" : "Get my tailored quote"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
            </Link>
            <Link to="/lenders" className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-line text-navy/70 font-bold hover:bg-paper-warm text-[13.5px]">
              More case studies
            </Link>
          </div>
        </div>
      </section>
      <section className="py-16 bg-paper-warm">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <SectionBadge>More stories</SectionBadge>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {CASE_STUDIES.filter(x => x.slug !== c.slug).slice(0, 3).map((o) => (
              <Link key={o.slug} to={`/case-studies/${o.slug}`} data-testid={`related-${o.slug}`}
                className="rounded-2xl bg-white border border-line p-6 hover:border-emerald/40 hover:-translate-y-1 transition-all">
                <div className="font-display font-extrabold text-[16px] text-navy tracking-[-0.015em] mb-1">{o.company}</div>
                <div className="text-[12px] text-navy/55 font-semibold mb-3">{o.focus}</div>
                <div className="inline-flex items-center gap-1 text-emerald-3 font-mono text-[10.5px] uppercase tracking-[0.14em] font-extrabold">Read <ArrowRight className="h-3 w-3" strokeWidth={2.6} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
